import { Component } from '@theme/component';

const FORCE_PARAM = 'vk_intl';
const STAY_KEY = 'shop_suggestion_stay';
const STAY_MS = 30 * 24 * 60 * 60 * 1000;
/** Visitor's country, 7 days on .valkental.com; the European shop's routing reads and writes the same cookie. */
const GEO_COOKIE = 'vk_geo';

/** Countries the European shop (valkental.com) sells to — keep in sync with SOLD in its snippets/vk-routing.liquid. */
const EU_COUNTRIES = ['DE', 'AT', 'CH', 'NL', 'BE', 'FR', 'LU', 'IT', 'ES', 'PT', 'SK', 'SI', 'SE', 'DK', 'FI', 'PL'];
/** Language subdomain of the European shop per country; every other EU country gets `en.` (its langHost()). */
const EU_HOSTS = { DE: '', AT: '', CH: 'ch', NL: 'nl', BE: 'nl', FR: 'fr' };
/** Products with a counterpart in the European shop (different handles). */
const EU_PRODUCTS = {
  '3-in-1-smart-jacket-men': '3in1-smart-jacket-herren',
  '3-in-1-smart-jacket-women': '3in1-smart-jacket-damen',
};
const TRACKING_PARAM = /^(utm_|gclid$|fbclid$)/;
const NAMES_WITH_THE = ['US', 'GB', 'NL'];

/**
 * Suggests the matching shop to visitors located outside this shop's country (see `sections/shop-suggestion.liquid`).
 * @typedef {{ market: string, heading: string, text: string, text_eu: string, go: string, us: string, uk: string, eu: string }} Copy
 * @typedef {{ dialog: HTMLDialogElement, title: HTMLElement, text: HTMLElement, go: HTMLAnchorElement }} Refs
 * @extends {Component<Refs>}
 */
class ShopSuggestionComponent extends Component {
  requiredRefs = ['dialog', 'title', 'text', 'go'];

  /** @type {Copy} */
  #copy = /** @type {Copy} */ ({});
  #forced = (new URLSearchParams(window.location.search).get(FORCE_PARAM) || '').toUpperCase();

  connectedCallback() {
    super.connectedCallback();
    this.#copy = JSON.parse(this.dataset.copy || '{}');
    this.refs.dialog.addEventListener('cancel', this.#onCancel);

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:section:select', this.#onEditorSelect);
      document.addEventListener('shopify:section:deselect', this.#onEditorDeselect);
      return;
    }

    if (/^[A-Z]{2}$/.test(this.#forced)) {
      this.#open(this.#forced);
      return;
    }
    if (this.#stayed()) return;
    this.#country().then((country) => {
      if (country) this.#open(country);
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog?.removeEventListener('cancel', this.#onCancel);
    document.removeEventListener('shopify:section:select', this.#onEditorSelect);
    document.removeEventListener('shopify:section:deselect', this.#onEditorDeselect);
  }

  stay() {
    if (!this.#forced && !window.Shopify?.designMode) {
      try {
        window.localStorage.setItem(STAY_KEY, String(Date.now()));
      } catch {
        // Storage blocked: the pop-up shows again on the next page.
      }
    }
    this.refs.dialog.close();
  }

  /** Esc counts as "Stay here". @param {Event} event */
  #onCancel = (event) => {
    event.preventDefault();
    this.stay();
  };

  #stayed() {
    try {
      return Date.now() - Number(window.localStorage.getItem(STAY_KEY) || 0) < STAY_MS;
    } catch {
      return false;
    }
  }

  /** @returns {Promise<string>} */
  async #country() {
    const cached = document.cookie.match(new RegExp(`(?:^|; )${GEO_COOKIE}=([A-Z]{2})`));
    if (cached) return cached[1];
    try {
      const trace = await (await fetch('/cdn-cgi/trace', { credentials: 'omit' })).text();
      const country = /(?:^|\n)loc=([A-Z]{2})/.exec(trace)?.[1] || '';
      if (country) {
        document.cookie = `${GEO_COOKIE}=${country}; path=/; domain=.valkental.com; max-age=604800; SameSite=Lax; Secure`;
      }
      return country;
    } catch {
      return '';
    }
  }

  /**
   * @param {string} country visitor's country
   * @returns {{ name: string, url: string, text: string, currency: string } | null}
   */
  #target(country) {
    const { market } = this.#copy;
    if (country === market) return null;
    const current = new URL(window.location.href);
    current.searchParams.delete(FORCE_PARAM);

    if (country === 'US' || country === 'GB') {
      // Same store, other market: the same page exists there.
      const us = country === 'US';
      const url = new URL(current.pathname + current.search, `https://${us ? 'us' : 'uk'}.valkental.com`);
      return { name: us ? this.#copy.us : this.#copy.uk, url: url.href, text: this.#copy.text, currency: us ? 'USD' : 'GBP' };
    }

    if (EU_COUNTRIES.includes(country)) {
      const handle = /\/products\/([^/?#]+)/.exec(current.pathname)?.[1];
      const path = handle && EU_PRODUCTS[handle] ? `/products/${EU_PRODUCTS[handle]}` : '/';
      const url = new URL(path, `https://${euHost(country)}`);
      current.searchParams.forEach((value, key) => {
        if (TRACKING_PARAM.test(key)) url.searchParams.set(key, value);
      });
      url.searchParams.set('country', country);
      return { name: this.#copy.eu, url: url.href, text: this.#copy.text_eu, currency: 'EUR' };
    }

    return null;
  }

  /** @param {string} country */
  #open(country) {
    const target = this.#target(country);
    if (!target) return;
    const { dialog, title, text, go } = this.refs;
    /** @param {string} template */
    const fill = (template) =>
      (template || '')
        .replaceAll('[country]', countryName(country))
        .replaceAll('[shop]', target.name)
        .replaceAll('[current]', this.#copy.market === 'GB' ? this.#copy.uk : this.#copy.us)
        .replaceAll('[currency]', target.currency);
    title.textContent = fill(this.#copy.heading);
    text.textContent = fill(target.text);
    go.textContent = fill(this.#copy.go);
    go.href = target.url;
    if (!dialog.open) dialog.showModal();
  }

  /** @param {Event} event */
  #onEditorSelect = (event) => {
    if (event.target instanceof Element && event.target.contains(this)) this.#open(this.#copy.market === 'US' ? 'GB' : 'US');
  };

  /** @param {Event} event */
  #onEditorDeselect = (event) => {
    if (event.target instanceof Element && event.target.contains(this)) this.refs.dialog.close();
  };
}

/**
 * Host of the European shop for a visitor: browser language with a real translation first (de, nl, fr),
 * else the country's language — the same choice as langHost() in that shop's routing.
 * @param {string} country
 */
function euHost(country) {
  const languages = navigator.languages?.length ? navigator.languages : [navigator.language || ''];
  for (const language of languages) {
    const base = String(language).slice(0, 2).toLowerCase();
    if (base === 'de') return country === 'CH' ? 'ch.valkental.com' : 'valkental.com';
    if (base === 'nl' || base === 'fr') return `${base}.valkental.com`;
  }
  const sub = country in EU_HOSTS ? EU_HOSTS[/** @type {keyof typeof EU_HOSTS} */ (country)] : 'en';
  return sub ? `${sub}.valkental.com` : 'valkental.com';
}

/** @param {string} country */
function countryName(country) {
  let name = country;
  try {
    name = new Intl.DisplayNames(['en'], { type: 'region' }).of(country) || country;
  } catch {
    // Old browser: the code stays.
  }
  return NAMES_WITH_THE.includes(country) ? `the ${name}` : name;
}

if (!customElements.get('shop-suggestion-component')) {
  customElements.define('shop-suggestion-component', ShopSuggestionComponent);
}
